/**
 * COMPONENT_SPEC §3 — the record. RULING-001 TERRITORY.
 * OWNER: web-frontend-engineer.
 *
 * ABSOLUTE CONSTRUCTION RULES, testable in the rendered output:
 *
 *  1. ZERO `input` / `select` / `textarea` elements — enabled or disabled —
 *     anywhere in this subtree. The unit control is two `<button>`s in a
 *     `role="radiogroup"`, never `<input type="radio">`.
 *  2. No padlock, no "read-only", no "locked", no "you cannot edit", no
 *     permission language, no greyed value, no edit icon. Authority is shown
 *     AS A NAME.
 *  3. `color.ink.tertiary` and `type.role.fine` are banned in this subtree —
 *     the page is AAA throughout.
 *  4. No commercial content of any kind.
 *  5. This module and `MeasurementAuthoringForm` are DIFFERENT COMPONENTS IN
 *     DIFFERENT MODULES and must not share a layout. Nothing here is imported
 *     by the authoring form and nothing there is imported by this file.
 */

import { useState } from 'react';
import type { MeasurementRecord as Record_ } from '@pt/core';
import { t, tDynamic, type CopyKey } from '@pt/copy';
import {
  MEASUREMENT_FIELDS,
  MEASUREMENT_GROUPS,
  measurementGroupCopyKey,
  pairContaining,
  toInches,
} from '@pt/view';
import { ageInWords, dateInWords, decimal } from '@pt/view';
import { Button, FactLine, Link, SelectionMark, Statement } from './primitives.tsx';

/* --------------------------------------------------------- ProvenanceLine */

export interface ProvenanceLineProps {
  readonly record: Record_;
  readonly successorTakenAt?: string | null;
  readonly onOpenSuccessor?: () => void;
}

/**
 * `EL-PROVENANCE`. The tailor's authority, shown as a NAME, never as a lock.
 * Renders above any measurement value, wherever a record appears.
 * A paragraph, not a heading — the record's outline stays h1 → h2 per group.
 */
export function ProvenanceLine({ record, successorTakenAt, onOpenSuccessor }: ProvenanceLineProps) {
  const methodKey = `el.provenance.method.${record.method}` as CopyKey;
  return (
    <div>
      <p className="pt-record__provenance">
        {t('el.provenance.line', {
          authorName: record.authoredByName,
          authorRole: t('global.role.master_tailor'),
          takenAtDate: dateInWords(record.takenAt),
          place: record.takenAtPlace ?? '',
        })}{' '}
        {tDynamic(methodKey)}
      </p>
      <FactLine>
        {record.status === 'superseded' && successorTakenAt != null ? (
          <>
            {t('el.provenance.standing.superseded', {
              successorDate: dateInWords(successorTakenAt),
            })}{' '}
            {onOpenSuccessor === undefined ? null : (
              <Link
                href="#successor"
                onNavigate={() => onOpenSuccessor()}
              >
                {t('el.provenance.standing.supersededLink')}
              </Link>
            )}
          </>
        ) : (
          t('el.provenance.standing.current')
        )}
      </FactLine>
    </div>
  );
}

/* The author's role is not a field on `MeasurementRecord`, and it does not need
 * to be: `API-ATELIER-MEAS-CREATE` and `-LOCK` are master_tailor-only, so a
 * locked record can only have been authored by one. Flagged for the architect
 * in the round report — if a `fitter` ever drafts, the role must come over the
 * wire rather than be inferred here. */
/* ---------------------------------------------------------- MeasurementRow */

export interface MeasurementRowProps {
  readonly label: string;
  readonly value: string;
  readonly ariaLabel: string;
  /** Asymmetry rows render TWO value columns, both always, even when equal. */
  readonly left?: string;
  readonly right?: string;
  readonly leftLabel?: string;
  readonly rightLabel?: string;
}

export function MeasurementRow({
  label,
  value,
  ariaLabel,
  left,
  right,
  leftLabel,
  rightLabel,
}: MeasurementRowProps) {
  return (
    <div className="pt-mrow" role="group" aria-label={ariaLabel}>
      <dt className="pt-mrow__label">{label}</dt>
      <dd className="pt-mrow__values">
        {left !== undefined && right !== undefined ? (
          <>
            <span className="pt-mrow__value">
              <span className="pt-mrow__side">{leftLabel}</span>
              {left}
            </span>
            <span className="pt-mrow__value">
              <span className="pt-mrow__side">{rightLabel}</span>
              {right}
            </span>
          </>
        ) : (
          <span className="pt-mrow__value">{value}</span>
        )}
      </dd>
    </div>
  );
}

/* -------------------------------------------------------------- UnitToggle */

export type Units = 'cm' | 'in';

export function UnitToggle({ units, onChange }: { units: Units; onChange: (u: Units) => void }) {
  const options: readonly Units[] = ['cm', 'in'];
  return (
    <div className="pt-units">
      <span className="pt-units__label" id="pt-units-label">
        {t('meas.units.label')}
      </span>
      <div role="radiogroup" aria-labelledby="pt-units-label" className="pt-units__group">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={units === option}
            tabIndex={units === option ? 0 : -1}
            className="pt-units__option"
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                event.preventDefault();
                onChange(option === 'cm' ? 'in' : 'cm');
              }
              if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                event.preventDefault();
                onChange(option === 'cm' ? 'in' : 'cm');
              }
            }}
            onClick={() => onChange(option)}
          >
            <SelectionMark checked={units === option} />
            <span>{option === 'cm' ? t('meas.units.cm') : t('meas.units.in')}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------- value presentation */

export function renderValue(
  fieldId: string,
  raw: string | number,
  units: Units,
): { text: string; unit: string } {
  const field = MEASUREMENT_FIELDS.find((f) => f.id === fieldId);
  if (field === undefined) return { text: String(raw), unit: '' };
  if (field.type === 'enum') {
    return { text: tDynamic(`meas.value.${fieldId}.${String(raw)}`), unit: '' };
  }
  if (field.type === 'text') return { text: String(raw), unit: '' };
  const numeric = typeof raw === 'number' ? raw : Number.parseFloat(String(raw));
  if (Number.isNaN(numeric)) return { text: String(raw), unit: '' };
  if (field.unit === 'cm' && units === 'in') {
    return { text: toInches(numeric), unit: t('meas.units.in') };
  }
  if (field.unit === 'cm') return { text: decimal(numeric, 1), unit: t('meas.units.cm') };
  if (field.unit === 'kg') return { text: decimal(numeric, 1), unit: field.unit };
  if (field.unit === 'deg') return { text: decimal(numeric, 1), unit: field.unit };
  return { text: decimal(numeric, 1), unit: '' };
}

/* --------------------------------------------------- MeasurementRecord */

export interface MeasurementRecordProps {
  readonly record: Record_;
  readonly clientName: string;
  readonly tailorName: string;
  readonly successorTakenAt?: string | null;
  readonly onOpenSuccessor?: () => void;
  readonly lastOpened?: { readonly byName: string; readonly at: string } | null;
  readonly history?: readonly Record_[];
  readonly onOpenRecord?: (recordId: string) => void;
  readonly onRemeasure?: () => void;
  readonly onPrivacyCopy?: () => void;
  readonly onPrivacyErase?: () => void;
  readonly onPrivacyFull?: () => void;
  /** Back-office client detail renders the SAME component, without the client's controls. */
  readonly readerIsStaff?: boolean;
}

/**
 * A typeset ledger. Not a form, not a disabled form, not the tailor's authoring
 * layout. Read-only BY CONSTRUCTION: there is no input element to disable.
 */
export function MeasurementRecordView({
  record,
  clientName,
  tailorName,
  successorTakenAt,
  onOpenSuccessor,
  lastOpened,
  history,
  onOpenRecord,
  onRemeasure,
  onPrivacyCopy,
  onPrivacyErase,
  onPrivacyFull,
  readerIsStaff = false,
}: MeasurementRecordProps) {
  const [units, setUnits] = useState<Units>('cm');
  const values = record.values;
  const rendered = new Set<string>();

  const ageMonths = Math.floor(
    (Date.now() - new Date(record.takenAt).getTime()) / (1000 * 60 * 60 * 24 * 30.4375),
  );

  return (
    <div className="pt-record">
      <ProvenanceLine
        record={record}
        successorTakenAt={successorTakenAt ?? null}
        {...(onOpenSuccessor === undefined ? {} : { onOpenSuccessor })}
      />

      {/* ST-MEAS-AGE — a FIELD, not an alert. Same position, same styling,
          no alert colour, no badge, no icon, no repeat prompt ([T-26]). */}
      {ageMonths > 18 ? (
        <Statement tone="field">
          {t('meas.age.statement', { ageInWords: ageInWords(record.takenAt), tailorName })}
        </Statement>
      ) : null}

      {/* ST-MEAS-LEGACY */}
      {record.provenance === 'legacy_import' && record.verifiedBy === null ? (
        <Statement tone="field">{t('meas.legacy.statement')}</Statement>
      ) : null}

      {/* ST-MEAS-PRIVACY — appears once, here. Both control actions at equal weight. */}
      {readerIsStaff ? null : (
        <section className="pt-record__privacy" aria-label={t('priv.statement.heading')}>
          <p>
            {lastOpened == null
              ? t('meas.privacy.statement.noAudit')
              : t('meas.privacy.statement.withAudit')}
          </p>
          <div className="pt-actions pt-actions--equal">
            <Button onClick={onPrivacyCopy}>{t('meas.privacy.copy')}</Button>
            <Button onClick={onPrivacyErase}>{t('meas.privacy.erase')}</Button>
          </div>
          <FactLine>
            <Link href="/privacy" onNavigate={() => onPrivacyFull?.()}>
              {t('meas.privacy.full')}
            </Link>
          </FactLine>
        </section>
      )}

      {/* ST-MEAS-UNITS — one sentence, once. Display only. */}
      <UnitToggle units={units} onChange={setUnits} />
      <Statement tone="field">{t('meas.units.statement')}</Statement>

      <hr className="pt-record__openingRule" />

      {MEASUREMENT_GROUPS.filter((g) => g.id !== 'notes').map((group) => {
        const groupFields = MEASUREMENT_FIELDS.filter(
          (f) => f.group === group.id && Object.prototype.hasOwnProperty.call(values, f.id),
        );
        if (groupFields.length === 0) return null;
        const heading = measurementGroupCopyKey(group.id);
        return (
          <section className="pt-record__group" key={group.id}>
            <h2 className="pt-record__groupHeading">{heading === undefined ? group.id : t(heading)}</h2>
            <dl className="pt-record__rows">
              {groupFields.map((field) => {
                if (rendered.has(field.id)) return null;
                const pair = pairContaining(field.id);
                if (pair !== undefined) {
                  const { leftId, rightId, labelFieldId } = pair;
                  if (
                    !Object.prototype.hasOwnProperty.call(values, leftId) ||
                    !Object.prototype.hasOwnProperty.call(values, rightId)
                  ) {
                    // fall through to the single-value row
                  } else {
                    rendered.add(leftId);
                    rendered.add(rightId);
                    const left = renderValue(leftId, values[leftId] as string | number, units);
                    const right = renderValue(rightId, values[rightId] as string | number, units);
                    const label = t(`meas.field.${labelFieldId}.label` as CopyKey);
                    return (
                      <MeasurementRow
                        key={`${leftId}-${rightId}`}
                        label={label}
                        value=""
                        left={`${left.text} ${left.unit}`.trim()}
                        right={`${right.text} ${right.unit}`.trim()}
                        leftLabel={t(`meas.field.${leftId}.label` as CopyKey)}
                        rightLabel={t(`meas.field.${rightId}.label` as CopyKey)}
                        ariaLabel={`${label}. ${t(`meas.field.${leftId}.label` as CopyKey)} ${left.text} ${left.unit}. ${t(`meas.field.${rightId}.label` as CopyKey)} ${right.text} ${right.unit}`}
                      />
                    );
                  }
                }
                rendered.add(field.id);
                const shown = renderValue(field.id, values[field.id] as string | number, units);
                const label = t(`meas.field.${field.id}.label` as CopyKey);
                return (
                  <MeasurementRow
                    key={field.id}
                    label={label}
                    value={`${shown.text} ${shown.unit}`.trim()}
                    ariaLabel={`${label}, ${shown.text} ${shown.unit}`.trim()}
                  />
                );
              })}
            </dl>
          </section>
        );
      })}

      {/* The tailor's notes, verbatim, in the same type as the rest. */}
      {typeof values['fitting_notes'] === 'string' && values['fitting_notes'] !== '' ? (
        <section className="pt-record__group">
          <h2 className="pt-record__groupHeading">
            {t('meas.notes.heading', { authorName: record.authoredByName })}
          </h2>
          <p className="pt-record__notes">{String(values['fitting_notes'])}</p>
        </section>
      ) : null}
      {typeof values['corrections_applied'] === 'string' && values['corrections_applied'] !== '' ? (
        <section className="pt-record__group">
          <h2 className="pt-record__groupHeading">{t('meas.field.corrections_applied.label')}</h2>
          <p className="pt-record__notes">{String(values['corrections_applied'])}</p>
        </section>
      ) : null}

      {/* ST-MEAS-LASTOPENED — dropped entirely, not softened, if no audit log. */}
      {lastOpened == null ? null : (
        <FactLine>
          {t('meas.lastopened', {
            openerName: lastOpened.byName,
            eventDate: dateInWords(lastOpened.at),
          })}
        </FactLine>
      )}

      {readerIsStaff || onRemeasure === undefined ? null : (
        <div className="pt-section">
          <Button onClick={onRemeasure}>{t('meas.remeasure.action')}</Button>
        </div>
      )}

      {history === undefined ? null : (
        <section className="pt-section" aria-label={t('meas.history.heading')}>
          <h2 className="pt-record__groupHeading">{t('meas.history.heading')}</h2>
          {history.length <= 1 ? (
            <FactLine>{t('meas.firstrecord.history')}</FactLine>
          ) : (
            <ul>
              {history.map((entry) => (
                <li key={entry.recordId} className="pt-row">
                  <Link
                    href={`/measurements/${entry.recordId}`}
                    onNavigate={() => onOpenRecord?.(entry.recordId)}
                  >
                    {t('meas.history.entry', {
                      authorName: entry.authoredByName,
                      takenAtDate: dateInWords(entry.takenAt),
                    })}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
